import type { Cpc25Report } from '../../../../types/LegalCase';
import { formatCurrency } from '../../../../utils/collateralFormatters';

// Dados dos gráficos "na mão" do relatório CPC 25 (mesmo espírito do ExpenseCompositionBar:
// barras segmentadas com div+Tailwind, sem lib de gráfico). Funções puras, testáveis.

export interface ChartSegment {
    key: string;
    label: string;
    value: number;
    formatted: string;
    pct: number;      // 0–100, participação no total
    color: string;
}

// Cor semântica fixa por classificação (severidade). Diferente da despesa (paleta cíclica),
// aqui a cor comunica risco.
const CLASSIFICATION_COLOR: Record<string, string> = {
    PROBABLE: '#DC2626',       // vermelho — provisiona
    POSSIBLE: '#F59E0B',       // âmbar
    REMOTE: '#10B981',         // verde
    NOT_APPLICABLE: '#6B7280', // cinza
    __NONE__: '#9CA3AF',       // não classificado
};

// Paleta cíclica para entidades (não há cor semântica por entidade).
const ENTITY_PALETTE = ['#3B82F6', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16', '#F97316'];

const pctOf = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);

// Arco de rosca (donut) por segmento: dashArray/dashOffset para desenhar cada fatia num
// <circle> com stroke. Puro e testável — o componente só consome os valores. `circumference`
// é 2πr; cada segmento ocupa uma fração da circunferência e começa onde o anterior terminou.
export interface DonutArc {
    key: string;
    label: string;
    formatted: string;
    color: string;
    pct: number;
    dashArray: string;   // "<comprimento do arco> <resto>"
    dashOffset: number;  // deslocamento acumulado (negativo, no sentido do stroke)
}

export const donutArcs = (segments: ChartSegment[], circumference: number): DonutArc[] => {
    let acc = 0;
    return segments.map((s) => {
        const arc = (s.pct / 100) * circumference;
        const item: DonutArc = {
            key: s.key,
            label: s.label,
            formatted: s.formatted,
            color: s.color,
            pct: s.pct,
            dashArray: `${arc} ${circumference - arc}`,
            // `acc || 0` evita -0 quando o offset é zero (o primeiro arco).
            dashOffset: acc === 0 ? 0 : -acc,
        };
        acc += arc;
        return item;
    });
};

// Provisão por entidade credora (soma dos polos). Só entidades com provisão > 0 entram.
export const provisionByEntity = (report: Cpc25Report): ChartSegment[] => {
    const rows = report.entities.map((e) => ({
        entity: e.creditorEntity,
        provision: e.poles.reduce((sum, p) => sum + p.provision, 0),
    }));
    const total = rows.reduce((sum, r) => sum + r.provision, 0);
    return rows
        .filter((r) => r.provision > 0)
        .sort((a, b) => b.provision - a.provision)
        .map((r, i) => ({
            key: r.entity,
            label: r.entity,
            value: r.provision,
            formatted: formatCurrency(r.provision),
            pct: pctOf(r.provision, total),
            color: ENTITY_PALETTE[i % ENTITY_PALETTE.length],
        }));
};

// Distribuição de casos por classificação (contagem), agregando todas as entidades/polos.
export const casesByClassification = (report: Cpc25Report): ChartSegment[] => {
    const counts: Record<string, { label: string; count: number }> = {};
    for (const entity of report.entities) {
        for (const pole of entity.poles) {
            for (const row of pole.rows) {
                const key = row.classification ?? '__NONE__';
                const label = row.classificationFormatted || 'Não avaliado';
                const entry = counts[key] || { label, count: 0 };
                entry.count += row.count;
                counts[key] = entry;
            }
        }
    }
    const total = Object.values(counts).reduce((sum, c) => sum + c.count, 0);
    // Ordem por severidade para leitura consistente. Chave inesperada (classificação nova do
    // backend) cai por último — indexOf -1 ordenaria antes de tudo, então mapeia para o fim.
    const order = ['PROBABLE', 'POSSIBLE', 'REMOTE', 'NOT_APPLICABLE', '__NONE__'];
    const rank = (key: string): number => {
        const i = order.indexOf(key);
        return i === -1 ? order.length : i;
    };
    return Object.entries(counts)
        .filter(([, c]) => c.count > 0)
        .sort(([a], [b]) => rank(a) - rank(b))
        .map(([key, c]) => ({
            key,
            label: c.label,
            value: c.count,
            formatted: String(c.count),
            pct: pctOf(c.count, total),
            color: CLASSIFICATION_COLOR[key] || CLASSIFICATION_COLOR.__NONE__,
        }));
};
